import PolicyLayout, { MetaBox, H2, H3, Ul } from '../components/PolicyLayout';

const PrivacyPolicyPage = () => (
  <PolicyLayout
    title="Privacy Policy"
    subtitle="mybook.rent — India's First Peer-to-Peer Book Rental Platform"
    updated="Last Updated: September 2026"
    testId="privacy-page"
  >
    <MetaBox
      rows={[
        ['Platform', 'mybook.rent'],
        ['Legal Entity', 'BOOKONRENT'],
        ['Registered Address', '312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087'],
        ['Governing Law', 'Digital Personal Data Protection (DPDP) Act, 2023 | Information Technology Act, 2000'],
        ['Grievance Contact', 'support@mybook.rent | +91 86607 85749'],
      ]}
    />

    <p>
      This Privacy Policy (&ldquo;Policy&rdquo;) sets out how mybook.rent, operated by BOOKONRENT (&ldquo;we&rdquo;,
      &ldquo;us&rdquo;, &ldquo;our&rdquo;), collects, uses, stores, and protects any personal information you provide
      when you visit or use our Platform at mybook.rent.
    </p>
    <p>
      mybook.rent is committed to protecting your privacy. We handle your personal data with care, transparency, and
      in full compliance with the Digital Personal Data Protection (DPDP) Act, 2023, the Information Technology Act,
      2000, and other applicable Indian laws.
    </p>
    <p>
      By using our Platform, you consent to the practices described in this Policy. This Policy applies to all current
      and former visitors, lenders, borrowers, and registered users of the Platform. Please review this Policy
      periodically — we may update it from time to time and will notify you of material changes.
    </p>
    <p>
      This Policy applies only to mybook.rent. If you follow links from our Platform to third-party websites, their
      privacy practices govern — we are not responsible for those sites.
    </p>

    <H2>1. What Information Do We Collect?</H2>
    <H3>1.1 Information You Provide Directly</H3>
    <p>When you register on or use our Platform, we may collect:</p>
    <Ul>
      <li><strong>Identity information:</strong> full name, date of birth, gender</li>
      <li><strong>Contact information:</strong> email address, phone number, residential address</li>
      <li><strong>Account credentials:</strong> username and password (stored securely, never in plain text)</li>
      <li><strong>Listing information (lenders):</strong> book titles, authors, editions, conditions, photographs, and rental pricing</li>
      <li><strong>Payment information:</strong> UPI IDs, bank account details, or card information processed through our payment gateway partners — we do not store full card or bank account numbers</li>
      <li><strong>Communication data:</strong> messages sent through the platform between lenders and borrowers, support queries, and feedback</li>
      <li><strong>Identity verification documents:</strong> where required for platform trust and safety (e.g. Aadhaar-linked phone verification)</li>
    </Ul>

    <H3>1.2 Information Collected Automatically</H3>
    <p>When you interact with our Platform, we automatically collect:</p>
    <Ul>
      <li><strong>Device and browser information:</strong> IP address, device type, operating system, browser type and version</li>
      <li><strong>Usage data:</strong> pages visited, features used, time spent on the Platform, search queries, and browsing patterns</li>
      <li><strong>Location data:</strong> approximate location based on IP address or, with your explicit consent, precise GPS location to enable neighbourhood-based book discovery</li>
      <li><strong>Transaction data:</strong> rental bookings, payment history, and platform activity logs</li>
      <li><strong>Cookie data:</strong> as described in Section 4 below</li>
    </Ul>

    <H3>1.3 Information From Third Parties</H3>
    <p>We may receive information about you from:</p>
    <Ul>
      <li>Social login providers (Google, Facebook) if you choose to register or log in using these services — including your name, email address, and profile picture</li>
      <li>Payment gateway partners — transaction confirmation details (not full card or bank data)</li>
      <li>Analytics and advertising partners — aggregated, anonymised usage data to improve our platform</li>
    </Ul>

    <H2>2. How We Use Your Information</H2>
    <p>We use the information we collect for the following purposes:</p>
    <Ul>
      <li>To create and manage your account and verify your identity</li>
      <li>To enable rental transactions between lenders and borrowers — including booking confirmation, pickup coordination, and return tracking</li>
      <li>To process payments and issue refunds in accordance with our Returns &amp; Refunds Policy</li>
      <li>To provide customer support and resolve disputes between users</li>
      <li>To send you transactional communications — booking confirmations, reminders, return due date alerts, and support responses</li>
      <li>To send you promotional communications — new features, offers, and platform updates — only where you have consented to receive these</li>
      <li>To personalise your experience — including book recommendations based on your rental history and preferences</li>
      <li>To improve and develop our Platform — analysing usage patterns, identifying bugs, and testing new features</li>
      <li>To ensure platform safety and prevent fraud, abuse, or policy violations</li>
      <li>To comply with our legal obligations under applicable Indian law, including the DPDP Act 2023</li>
    </Ul>
    <p>
      We will never sell your personal data to third parties for commercial purposes. Your data is yours — we are its
      custodians, not its owners.
    </p>

    <H2>3. How We Share Your Information</H2>
    <H3>3.1 With Other Users (Lenders &amp; Borrowers)</H3>
    <p>To enable rental transactions, limited information is shared between lenders and borrowers:</p>
    <Ul>
      <li>Borrowers can see a lender&rsquo;s first name, general neighbourhood area, and listing details.</li>
      <li>Lenders can see a borrower&rsquo;s first name and booking details once a rental is confirmed.</li>
      <li>Full addresses are only shared with confirmed, active rental parties — not with general visitors or other users.</li>
    </Ul>

    <H3>3.2 With Service Providers</H3>
    <p>
      We share data with trusted third-party service providers who help us operate the Platform — including payment
      processors, cloud hosting providers, analytics tools, and communication services. These providers are
      contractually bound to use your data only for the purposes we specify and in compliance with applicable law.
    </p>

    <H3>3.3 For Legal Compliance</H3>
    <p>
      We may disclose your information to government authorities, law enforcement agencies, or regulators when
      required to do so by law — including for identity verification, cybercrime investigation, or court orders under
      the Information Technology Act 2000 or DPDP Act 2023.
    </p>

    <H3>3.4 Business Transfers</H3>
    <p>
      In the event of a merger, acquisition, or sale of all or part of our business, your personal data may be
      transferred to the acquiring entity as part of that transaction. We will notify you of any such transfer and
      the applicable privacy terms.
    </p>

    <H3>3.5 With Your Consent</H3>
    <p>We will not share your information with any other third party without your explicit consent.</p>

    <H2>4. Cookies &amp; Tracking Technologies</H2>
    <p>Cookies are small text files placed on your device when you visit our Platform. We use cookies to:</p>
    <Ul>
      <li>Remember your login session and preferences</li>
      <li>Analyse platform traffic and usage patterns to improve our service</li>
      <li>Enable personalised content and book recommendations</li>
      <li>Support secure payment processing</li>
    </Ul>
    <p>
      You can control or disable cookies through your browser settings. Note that disabling cookies may affect some
      features of the Platform. We use both session cookies (deleted when you close your browser) and persistent
      cookies (retained for a set period).
    </p>
    <p>
      We do not use cookies to collect sensitive personal information or to track you across third-party websites for
      advertising purposes without your explicit consent.
    </p>

    <H2>5. How We Protect Your Data</H2>
    <p>
      We implement appropriate technical and organisational security measures to protect your personal data from
      unauthorised access, loss, alteration, or disclosure. These include:
    </p>
    <Ul>
      <li>Encryption of data in transit (HTTPS/TLS) and at rest</li>
      <li>Secure storage of passwords using industry-standard hashing</li>
      <li>Access controls ensuring only authorised personnel can access personal data</li>
      <li>Regular security reviews and vulnerability assessments</li>
    </Ul>
    <p>
      However, no method of data transmission or storage is 100% secure. While we strive to protect your data, we
      cannot guarantee absolute security. If you suspect your account has been compromised, please contact us
      immediately at support@mybook.rent.
    </p>

    <H2>6. How Long We Retain Your Data</H2>
    <Ul>
      <li>Account data is retained for as long as your account is active and for a reasonable period thereafter to comply with legal obligations.</li>
      <li>Rental transaction records are retained for a minimum of 3 years for audit and dispute resolution purposes.</li>
      <li>Payment records are retained as required by applicable financial and tax laws in India.</li>
      <li>If you delete your account, we will delete or anonymise your personal data within 30 days, except where retention is required by law.</li>
    </Ul>

    <H2>7. Your Rights Under the DPDP Act 2023</H2>
    <p>
      The Digital Personal Data Protection (DPDP) Act, 2023 grants you the following rights in relation to your
      personal data held by mybook.rent:
    </p>
    <Ul>
      <li><strong>Right to Access:</strong> You may request a summary of the personal data we hold about you and how it is being processed.</li>
      <li><strong>Right to Correction:</strong> You may request correction of any inaccurate or incomplete personal data we hold.</li>
      <li><strong>Right to Erasure:</strong> You may request deletion of your personal data, subject to our legal obligations to retain certain records.</li>
      <li><strong>Right to Grievance Redressal:</strong> You have the right to raise a grievance with our Grievance Officer regarding our data practices.</li>
      <li><strong>Right to Nominate:</strong> You may nominate another individual to exercise these rights on your behalf in the event of your death or incapacity.</li>
      <li><strong>Right to Withdraw Consent:</strong> Where processing is based on your consent, you may withdraw consent at any time. Withdrawal does not affect the lawfulness of processing before withdrawal.</li>
    </Ul>
    <p>To exercise any of these rights, please contact our Grievance Officer at the details provided in Section 10.</p>

    <H2>8. Children&rsquo;s Privacy</H2>
    <p>
      mybook.rent is not intended for use by persons under the age of 18 without parental or guardian consent. We do
      not knowingly collect personal data from children under 18. If we become aware that we have inadvertently
      collected data from a child without appropriate consent, we will delete it promptly.
    </p>
    <p>
      Parents or guardians who believe their child has provided personal data to mybook.rent without consent should
      contact us at support@mybook.rent.
    </p>

    <H2>9. Controlling Your Personal Information</H2>
    <Ul>
      <li>You can update or correct your account information at any time by logging into your account and visiting &lsquo;My Profile&rsquo;.</li>
      <li>To opt out of promotional communications, click &lsquo;Unsubscribe&rsquo; in any marketing email or contact support@mybook.rent.</li>
      <li>To request deletion of your account and associated personal data, contact support@mybook.rent with the subject line &lsquo;Account Deletion Request&rsquo;.</li>
      <li>To withdraw consent for location-based services, update your device or browser location permissions at any time.</li>
      <li>To opt out of personalised recommendations, contact us and we will adjust your account settings accordingly.</li>
    </Ul>

    <H2>10. Grievance Officer</H2>
    <p>
      In accordance with the Information Technology Act 2000, the DPDP Act 2023, and the IT (Intermediary Guidelines
      and Digital Media Ethics Code) Rules 2021, we have designated a Grievance Officer to address any privacy-related
      concerns:
    </p>
    <MetaBox
      rows={[
        ['Name', 'P. Kalyan Chaitanya'],
        ['Designation', 'Founder & Grievance Officer, BOOKONRENT'],
        ['Email', 'support@mybook.rent'],
        ['Phone / WhatsApp', '+91 86607 85749'],
        ['Address', '312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087'],
        ['Response Time', 'Grievances acknowledged within 48 hours; resolved within 30 days'],
      ]}
    />
    <p>
      If you believe any personal data we hold about you is incorrect, incomplete, or being processed unlawfully —
      or if you have any question or concern about this Policy — please contact the Grievance Officer using the
      details above. We take all privacy concerns seriously and will respond promptly.
    </p>

    <H2>11. Changes to This Privacy Policy</H2>
    <p>
      We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or
      applicable law. When we make material changes, we will update the &ldquo;Last Updated&rdquo; date above and,
      where appropriate, notify registered users by email or through a prominent notice on the Platform.
    </p>
    <p>
      We encourage you to review this Policy periodically. Your continued use of the Platform after any changes
      constitutes your acceptance of the updated Policy.
    </p>
  </PolicyLayout>
);

export default PrivacyPolicyPage;
